/**
 * @file きき分けあそび (epic_24) の白鍵 9 和音と色の対応 (正典データ)。
 * @why: 『新・絶対音感プログラム』の楽譜 1 (s.60) と表 1 (s.63) の転記。並び順 = 練習の
 *   追加順 (R4/R10。順番は原則まもる s.62)。構成音は中央音域のみ (R8、s.81)。
 *   ここを書き換えるときは必ず原典画面 (本だな onkan-program s.60/s.63) と照合する。
 * @failure: 転記ミスは方式そのものを壊す。エンジン・UI とも本配列の順序に依存する
 *   (active は常にこの並びの先頭からの部分列)。
 */

export type ChordId =
  | "domiso"
  | "dofara"
  | "shireso"
  | "radofa"
  | "resoshi"
  | "misodo"
  | "farado"
  | "soshire"
  | "sodomi";

export interface KikiwakeChord {
  id: ChordId;
  /** 和音の呼び名 (例: ドミソ)。親向け表示・memo に使う。 */
  chordName: string;
  /** ハタの色 (表 1 = s.63 の表記)。 */
  colorName: string;
  /** 子ども向けのひらがな読み (読み上げ補助 + 色覚配慮の併記)。 */
  colorKana: string;
  /** ボタンの塗り色。 */
  css: string;
  /** 塗りの上の文字色 (明るい色は濃い文字)。 */
  textOnColor: string;
  /** 構成音 (中央音域、下から)。s.60 楽譜 1 の読み取りどおり。 */
  notes: [string, string, string];
}

/** 楽譜 1 (s.60) = 表 1 (s.63) の順。この順にしか追加しない (R4)。 */
export const KIKIWAKE_CHORDS: readonly KikiwakeChord[] = [
  { id: "domiso", chordName: "ドミソ", colorName: "赤", colorKana: "あか", css: "#d63c3c", textOnColor: "#fff", notes: ["C4", "E4", "G4"] },
  { id: "dofara", chordName: "ドファラ", colorName: "黄色", colorKana: "きいろ", css: "#f2c53d", textOnColor: "#4a3a10", notes: ["C4", "F4", "A4"] },
  { id: "shireso", chordName: "シレソ", colorName: "青", colorKana: "あお", css: "#3b6fd4", textOnColor: "#fff", notes: ["B3", "D4", "G4"] },
  { id: "radofa", chordName: "ラドファ", colorName: "黒", colorKana: "くろ", css: "#2b2b2b", textOnColor: "#fff", notes: ["A3", "C4", "F4"] },
  { id: "resoshi", chordName: "レソシ", colorName: "緑", colorKana: "みどり", css: "#3d9950", textOnColor: "#fff", notes: ["D4", "G4", "B4"] },
  { id: "misodo", chordName: "ミソド", colorName: "オレンジ", colorKana: "オレンジ", css: "#ee8b2f", textOnColor: "#fff", notes: ["E4", "G4", "C5"] },
  { id: "farado", chordName: "ファラド", colorName: "紫", colorKana: "むらさき", css: "#8a56b0", textOnColor: "#fff", notes: ["F4", "A4", "C5"] },
  { id: "soshire", chordName: "ソシレ", colorName: "ピンク", colorKana: "ピンク", css: "#ef86ae", textOnColor: "#4a2233", notes: ["G4", "B4", "D5"] },
  { id: "sodomi", chordName: "ソドミ", colorName: "茶色", colorKana: "ちゃいろ", css: "#8b5a33", textOnColor: "#fff", notes: ["G4", "C5", "E5"] },
] as const;

const byId = new Map(KIKIWAKE_CHORDS.map((c) => [c.id, c]));

export function kikiwakeChord(id: ChordId): KikiwakeChord {
  const c = byId.get(id);
  if (!c) throw new Error(`unknown chord: ${id}`);
  return c;
}

/** 追加順の位置 (0 始まり)。追加順 = 本配列順なので「後から始めた和音」判定に使う (R17)。 */
export function chordOrder(id: ChordId): number {
  return KIKIWAKE_CHORDS.findIndex((c) => c.id === id);
}

export function isChordId(v: unknown): v is ChordId {
  return typeof v === "string" && byId.has(v as ChordId);
}
